import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { AnimatePresence, motion } from 'framer-motion';
import api from '../../config/api';
import {
  FiActivity,
  FiAlertCircle,
  FiClock,
  FiCpu,
  FiRefreshCw,
  FiZap,
  FiChevronDown,
  FiChevronUp,
  FiUser,
} from 'react-icons/fi';
import { ButtonSpinner } from '../LoadingButton';
import {
  actionLabel,
  formatAiDate,
  formatDuration,
  formatTokens,
  parseUsageJson,
  providerLabel,
  statusClass,
} from './aiWatchUtils';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';

const POLL_FAST = 3000;
const POLL_SLOW = 15000;

function StatusPill({ status }) {
  const cls = statusClass(status);
  return (
    <span className={`ai-watch-pill ai-watch-pill--${cls}`}>
      {(status === 'running' || status === 'pending') && (
        <span className="ai-watch-pill-dot" aria-hidden />
      )}
      {status}
    </span>
  );
}

function TokenBar({ label, value, max, tone = 'default' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="ai-watch-token-bar">
      <div className="ai-watch-token-bar-head">
        <span>{label}</span>
        <strong>{formatTokens(value)}</strong>
      </div>
      <div className="ai-watch-token-bar-track">
        <motion.div
          className={`ai-watch-token-bar-fill ai-watch-token-bar-fill--${tone}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

function ActivityRow({ entry, expanded, onToggle }) {
  const usage = parseUsageJson(entry.usage_json);
  const reasoning =
    entry.reasoning_tokens ||
    usage?.completion_tokens_details?.reasoning_tokens ||
    0;

  return (
    <motion.article
      layout
      className={`ai-watch-row ai-watch-row--${statusClass(entry.status)}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.25 }}
    >
      <button type="button" className="ai-watch-row-main" onClick={onToggle}>
        <div className="ai-watch-row-status">
          <StatusPill status={entry.status} />
        </div>
        <div className="ai-watch-row-body">
          <strong>{entry.request_summary || actionLabel(entry.action_key)}</strong>
          <span className="ai-watch-row-meta">
            {providerLabel(entry.provider)}
            {entry.model ? ` · ${entry.model}` : ''}
            {entry.user_name || entry.user_email ? (
              <>
                {' · '}
                <FiUser aria-hidden /> {entry.user_name || entry.user_email}
              </>
            ) : null}
          </span>
        </div>
        <div className="ai-watch-row-metrics">
          {entry.status === 'completed' && (
            <span className="ai-watch-row-tokens">{formatTokens(entry.total_tokens)} tok</span>
          )}
          <span className="ai-watch-row-time">{formatAiDate(entry.created_at)}</span>
          {expanded ? <FiChevronUp aria-hidden /> : <FiChevronDown aria-hidden />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="ai-watch-row-detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <dl className="ai-watch-detail-grid">
              <div>
                <dt>Action</dt>
                <dd>{actionLabel(entry.action_key)}</dd>
              </div>
              <div>
                <dt>Trigger</dt>
                <dd>{entry.trigger_source || '—'}</dd>
              </div>
              <div>
                <dt>Resource</dt>
                <dd>
                  {entry.resource_type
                    ? `${entry.resource_type} #${entry.resource_id || '—'}`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>Duration</dt>
                <dd>{formatDuration(entry.duration_ms)}</dd>
              </div>
              <div>
                <dt>Prompt tokens</dt>
                <dd>{formatTokens(entry.prompt_tokens)}</dd>
              </div>
              <div>
                <dt>Completion tokens</dt>
                <dd>{formatTokens(entry.completion_tokens)}</dd>
              </div>
              <div>
                <dt>Total tokens</dt>
                <dd>{formatTokens(entry.total_tokens)}</dd>
              </div>
              {reasoning > 0 && (
                <div>
                  <dt>Reasoning tokens</dt>
                  <dd>{formatTokens(reasoning)}</dd>
                </div>
              )}
              {entry.started_at && (
                <div>
                  <dt>Started</dt>
                  <dd>{formatAiDate(entry.started_at)}</dd>
                </div>
              )}
              {entry.completed_at && (
                <div>
                  <dt>Completed</dt>
                  <dd>{formatAiDate(entry.completed_at)}</dd>
                </div>
              )}
            </dl>
            {entry.error_message && (
              <p className="ai-watch-row-error">
                <FiAlertCircle aria-hidden /> {entry.error_message}
              </p>
            )}
            {usage && (
              <pre className="ai-watch-usage-json">{JSON.stringify(usage, null, 2)}</pre>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

function QueueCard({ entry }) {
  return (
    <motion.div
      className="ai-watch-queue-card"
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.3 }}
    >
      <StatusPill status={entry.status} />
      <div>
        <strong>{entry.request_summary || actionLabel(entry.action_key)}</strong>
        <span>{providerLabel(entry.provider)} · {entry.user_name || entry.user_email || 'System'}</span>
      </div>
      <span className="ai-watch-queue-time">{formatAiDate(entry.created_at)}</span>
    </motion.div>
  );
}

const AiWatchDashboard = () => {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState(null);

  const pollInterval = () => {
    const s = queryClient.getQueryData(['ai-watch-status']);
    const active = (s?.counts?.pending || 0) + (s?.counts?.running || 0);
    return active > 0 ? POLL_FAST : POLL_SLOW;
  };

  const { data: status, isLoading: statusLoading, isFetching: statusFetching } = useQuery(
    ['ai-watch-status'],
    () => api.get('/permissions/ai-watch/status').then((r) => r.data),
    {
      refetchInterval: pollInterval,
      refetchOnWindowFocus: true,
      staleTime: 2000,
    }
  );

  const { data: entries = [], isLoading: entriesLoading, isFetching: entriesFetching } = useQuery(
    ['ai-watch-log'],
    () => api.get('/permissions/ai-watch?limit=80').then((r) => r.data),
    {
      refetchInterval: pollInterval,
      refetchOnWindowFocus: true,
      staleTime: 2000,
    }
  );

  const {
    pageItems: pagedEntries,
    pagination: watchPagination,
    setPage: setWatchPage,
  } = useClientPager(entries);

  const counts = status?.counts || {};
  const tokens = useMemo(
    () => status?.last_30d || status?.last_24h || {},
    [status?.last_30d, status?.last_24h]
  );
  const retentionDays = status?.retention_days || 30;
  const activeQueue = status?.active_queue || [];
  const isLive = (counts.pending || 0) + (counts.running || 0) > 0;

  const tokenMax = useMemo(
    () => Math.max(tokens.prompt_tokens || 0, tokens.completion_tokens || 0, tokens.reasoning_tokens || 0, 1),
    [tokens]
  );

  const refreshAll = () => {
    queryClient.invalidateQueries(['ai-watch-status']);
    queryClient.invalidateQueries(['ai-watch-log']);
  };

  if (statusLoading && !status) {
    return <div className="loading">Loading AI Watch…</div>;
  }

  return (
    <div className="ai-watch-dashboard">
      <div className="ai-watch-toolbar">
        <button type="button" className="btn btn-secondary" onClick={refreshAll}>
          {statusFetching || entriesFetching ? <ButtonSpinner /> : <FiRefreshCw />} Refresh
        </button>
        {isLive && (
          <span className="ai-watch-live">
            <span className="ai-watch-live-dot" aria-hidden />
            Live — {counts.running || 0} running, {counts.pending || 0} pending
          </span>
        )}
      </div>

      <div className="ai-watch-status-grid">
        <motion.div
          className={`ai-watch-card${counts.running ? ' is-active' : ''}`}
          layout
        >
          <FiZap aria-hidden />
          <div>
            <span>Running</span>
            <strong>{counts.running ?? 0}</strong>
          </div>
        </motion.div>
        <motion.div
          className={`ai-watch-card${counts.pending ? ' is-pending' : ''}`}
          layout
        >
          <FiClock aria-hidden />
          <div>
            <span>Pending</span>
            <strong>{counts.pending ?? 0}</strong>
          </div>
        </motion.div>
        <div className="ai-watch-card">
          <FiActivity aria-hidden />
          <div>
            <span>Completed ({retentionDays}d)</span>
            <strong>{tokens.calls ?? 0}</strong>
          </div>
        </div>
        <div className="ai-watch-card">
          <FiCpu aria-hidden />
          <div>
            <span>Tokens ({retentionDays}d)</span>
            <strong>{formatTokens(tokens.total_tokens ?? 0)}</strong>
          </div>
        </div>
        <div className={`ai-watch-card${counts.failed ? ' is-error' : ''}`}>
          <FiAlertCircle aria-hidden />
          <div>
            <span>Failed</span>
            <strong>{counts.failed ?? 0}</strong>
          </div>
        </div>
      </div>

      <section className="ai-watch-section">
        <h2>Token usage (last {retentionDays} days)</h2>
        <div className="ai-watch-token-panel">
          <TokenBar label="Prompt" value={tokens.prompt_tokens || 0} max={tokenMax} tone="prompt" />
          <TokenBar label="Completion" value={tokens.completion_tokens || 0} max={tokenMax} tone="completion" />
          {(tokens.reasoning_tokens || 0) > 0 && (
            <TokenBar label="Reasoning" value={tokens.reasoning_tokens || 0} max={tokenMax} tone="reasoning" />
          )}
        </div>
        {(status?.by_provider?.length > 0 || status?.by_action?.length > 0) && (
          <div className="ai-watch-breakdown">
            {status.by_provider?.length > 0 && (
              <div>
                <h3>By provider</h3>
                <ul>
                  {status.by_provider.map((row) => (
                    <li key={row.provider}>
                      {providerLabel(row.provider)} — {row.calls} calls, {formatTokens(row.total_tokens)} tok
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {status.by_action?.length > 0 && (
              <div>
                <h3>By action</h3>
                <ul>
                  {status.by_action.map((row) => (
                    <li key={row.action_key}>
                      {actionLabel(row.action_key)} — {row.calls} calls, {formatTokens(row.total_tokens)} tok
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      {activeQueue.length > 0 && (
        <section className="ai-watch-section ai-watch-queue-section">
          <h2>
            <span className="ai-watch-queue-pulse" aria-hidden />
            Active queue
          </h2>
          <div className="ai-watch-queue-list">
            <AnimatePresence>
              {activeQueue.map((entry) => (
                <QueueCard key={entry.id} entry={entry} />
              ))}
            </AnimatePresence>
          </div>
        </section>
      )}

      <section className="ai-watch-section">
        <h2>Activity log (last {retentionDays} days)</h2>
        {entriesLoading && entries.length === 0 ? (
          <div className="loading">Loading activity…</div>
        ) : entries.length === 0 ? (
          <p className="ai-watch-empty">No AI activity in the last {retentionDays} days. Run a match or use Resume Builder to see entries here.</p>
        ) : (
          <div className="ai-watch-log">
            <AnimatePresence initial={false}>
              {pagedEntries.map((entry) => (
                <ActivityRow
                  key={entry.id}
                  entry={entry}
                  expanded={expandedId === entry.id}
                  onToggle={() =>
                    setExpandedId((prev) => (prev === entry.id ? null : entry.id))
                  }
                />
              ))}
            </AnimatePresence>
          </div>
        )}
        <ListPager
          page={watchPagination.page}
          pageCount={watchPagination.pageCount}
          total={watchPagination.total}
          limit={watchPagination.limit}
          onPageChange={setWatchPage}
        />
      </section>
    </div>
  );
};

export default AiWatchDashboard;
